import type { ParameterDirection, SweetSpotParameter, SweetSpotParameterConfig } from './types';

const DEFAULT_RQ_PARAMETERS: SweetSpotParameter[] = [
  { id: 'rq.toc', key: 'toc', group: 'rq', label: 'TOC（总有机碳）', unit: '%', enabled: true, weight: 0.35, direction: 'larger_is_better' },
  { id: 'rq.porosity', key: 'porosity', group: 'rq', label: '孔隙度', unit: '%', enabled: true, weight: 0.30, direction: 'larger_is_better' },
  { id: 'rq.brittleness', key: 'brittleness', group: 'rq', label: 'BI（脆性指数）', unit: '%', enabled: true, weight: 0.20, direction: 'larger_is_better' },
  { id: 'rq.clayContent', key: 'clayContent', group: 'rq', label: '黏土含量', unit: '%', enabled: false, weight: 0.15, direction: 'smaller_is_better' },
];

const DEFAULT_CQ_PARAMETERS: SweetSpotParameter[] = [
  { id: 'cq.youngModulus', key: 'youngModulus', group: 'cq', label: 'YM（杨氏模量）', unit: 'GPa', enabled: true, weight: 0.35, direction: 'larger_is_better' },
  { id: 'cq.poissonRatio', key: 'poissonRatio', group: 'cq', label: 'PR（泊松比）', unit: '无量纲', enabled: true, weight: 0.25, direction: 'smaller_is_better' },
  { id: 'cq.brittleness', key: 'brittleness', group: 'cq', label: 'BI（脆性指数）', unit: '%', enabled: true, weight: 0.20, direction: 'larger_is_better' },
  { id: 'cq.minHorizontalStress', key: 'minHorizontalStress', group: 'cq', label: 'Shmin（最小水平主应力）', unit: 'MPa', enabled: true, weight: 0.20, direction: 'smaller_is_better' },
];

const STORAGE_PREFIX = 'fracsight:sweetspot-parameters:';
const cloneParameters = (parameters: SweetSpotParameter[]) => parameters.map((parameter) => ({ ...parameter }));

export function createDefaultParameterConfig(): SweetSpotParameterConfig {
  return { rq: cloneParameters(DEFAULT_RQ_PARAMETERS), cq: cloneParameters(DEFAULT_CQ_PARAMETERS) };
}

function isDirection(value: unknown): value is ParameterDirection {
  return value === 'larger_is_better' || value === 'smaller_is_better' || value === 'target_range';
}

function mergeStoredParameters(defaults: SweetSpotParameter[], stored: unknown): SweetSpotParameter[] {
  if (!Array.isArray(stored)) return cloneParameters(defaults);
  const merged = defaults.map((parameter) => {
    const saved = stored.find((item) => item && typeof item === 'object' && item.id === parameter.id);
    if (!saved) return { ...parameter };
    return {
      ...parameter,
      enabled: typeof saved.enabled === 'boolean' ? saved.enabled : parameter.enabled,
      weight: typeof saved.weight === 'number' && Number.isFinite(saved.weight) && saved.weight >= 0 ? saved.weight : parameter.weight,
      direction: isDirection(saved.direction) ? saved.direction : parameter.direction,
      targetMin: typeof saved.targetMin === 'number' ? saved.targetMin : undefined,
      targetMax: typeof saved.targetMax === 'number' ? saved.targetMax : undefined,
    };
  });
  const custom = stored.filter((item): item is SweetSpotParameter => {
    if (!item || typeof item !== 'object') return false;
    const candidate = item as Partial<SweetSpotParameter>;
    return candidate.custom === true && typeof candidate.id === 'string'
      && typeof candidate.key === 'string' && candidate.key.startsWith('custom_')
      && typeof candidate.label === 'string' && (candidate.group === 'rq' || candidate.group === 'cq');
  }).map((parameter) => ({
    ...parameter,
    enabled: typeof parameter.enabled === 'boolean' ? parameter.enabled : true,
    weight: typeof parameter.weight === 'number' && Number.isFinite(parameter.weight) && parameter.weight >= 0 ? parameter.weight : 0,
    direction: isDirection(parameter.direction) ? parameter.direction : 'larger_is_better' as ParameterDirection,
  }));
  return [...merged, ...custom];
}

export function loadParameterConfig(projectId?: number): SweetSpotParameterConfig {
  const defaults = createDefaultParameterConfig();
  if (!projectId) return defaults;
  const storageKey = `${STORAGE_PREFIX}${projectId}`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      localStorage.removeItem(storageKey);
      return defaults;
    }
    const stored = parsed as Partial<SweetSpotParameterConfig>;
    return { rq: mergeStoredParameters(defaults.rq, stored.rq), cq: mergeStoredParameters(defaults.cq, stored.cq) };
  } catch {
    try { localStorage.removeItem(storageKey); } catch { /* storage remains unavailable */ }
    return defaults;
  }
}

export function saveParameterConfig(projectId: number, config: SweetSpotParameterConfig): boolean {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${projectId}`, JSON.stringify(config));
    return true;
  } catch {
    return false;
  }
}
